import type {Component} from '@kpsys/angularjs-register';
import type AvailabilityDataService from '../availability.data-service';
import type {
    Auth,
    AvailabilityAwareExemplar,
    Exemplar,
    LabeledIdentified,
    RichDocument
} from 'typings/portaro.be.types';
import {ngAsync} from 'shared/utils/ng-@decorators';
import type FinishedResponseInteractionService from '../../commons/services/finished-response-interaction.service';
import type LoanRequestService from '../../commons/services/loan-request.service';
import type ExemplarPlacementService from '../exemplar-placement.service';
import type {ExemplarsType} from '../exemplar-list';
import type ExemplarFilter from '../exemplar-filter';
import type ExemplarService from '../exemplar.service';
import type {IOnInit} from 'angular';

interface PlacementLoadableExemplar extends Exemplar {
    loadingPlacement: boolean;
}

export class KpExemplarsListComponentController implements KpExemplarsListComponentBindings, IOnInit {
    public exemplars: Exemplar[];
    public type: ExemplarsType;
    public properties: LabeledIdentified<string>[];
    public document: RichDocument;
    public exemplarFilter: ExemplarFilter;

    /*@ngInject*/
    constructor(
        private availabilityDataService: AvailabilityDataService,
        private currentAuth: Auth,
        private finishedResponseInteractionService: FinishedResponseInteractionService,
        private exemplarService: ExemplarService,
        private loanRequestService: LoanRequestService,
        private exemplarPlacementService: ExemplarPlacementService) {
    }

    public $onInit(): void {
        if (this.exemplars) {
            this.exemplars
                .filter((exemplar) => !(exemplar as AvailabilityAwareExemplar).availability)
                .forEach((exemplar) => {
                        this.availabilityDataService.getByExemplarAndRequester(exemplar.id, this.currentAuth.activeUser.id)
                            .then((availability) => (exemplar as AvailabilityAwareExemplar).availability = availability)
                    }
                );
        }
    }

    public discardExemplar(exemplar: Exemplar) {
        return this.exemplarService.discardExemplar(exemplar);
    }

    public editExemplar(exemplar: Exemplar) {
        return this.exemplarService.editExemplar(exemplar);
    }

    public deleteExemplar(exemplar: Exemplar) {
        return this.exemplarService.deleteExemplar(exemplar);
    }

    public restoreExemplar(exemplar: Exemplar) {
        return this.exemplarService.restoreExemplar(exemplar);
    }

    public async requestExemplar(exemplar: Exemplar) {
        try {
            const actionResponse = await this.loanRequestService.requestExemplar(exemplar.id);
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
        }
    }

    @ngAsync()
    public async loadPlacement(exemplar: PlacementLoadableExemplar): Promise<void> {
        exemplar.loadingPlacement = true;

        await this.exemplarPlacementService.loadPlacementIntoExemplar(exemplar);

        exemplar.loadingPlacement = false;
    }

    public showColumn(exemplars: Exemplar[], {id}): boolean {
        return exemplars.some((exemplar) => {
            if (angular.isObject(exemplar[id]) && angular.isDefined(exemplar[id].text)) {
                return exemplar[id].text.length > 0;
            }

            return angular.isDefined(exemplar[id]) && exemplar[id] !== null;
        });
    }
}

/**
 * @ngdoc component
 * @name kpExemplarList
 * @module uc.record
 *
 * @param {Exemplar[]} exemplars Exemplars
 * @param {ExemplarsType} type Type of exemplars
 * @param {LabeledIdentified[]} properties Properties of exemplars to display
 * @param {RichDocument} document Document
 * @param {any} exemplarFilter Exemplars filter
 *
 * @description
 *
 */
export default class KpExemplarsListComponent implements Component<KpExemplarsListComponentBindings> {
    public static readonly componentName = 'kpExemplarList';

    public templateUrl = require('./kp-exemplars-list.tpl.ftl');

    public bindings = {
        exemplars: '<',
        type: '<',
        properties: '<',
        document: '<',
        exemplarFilter: '<'
    } as const;

    public controller = KpExemplarsListComponentController;
}

interface KpExemplarsListComponentBindings {
    exemplars: Exemplar[];
    type: ExemplarsType;
    properties: LabeledIdentified<string>[];
    document: RichDocument;
    exemplarFilter: ExemplarFilter;
}